import { toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { useEffect, useState } from 'react'
import { SWRResponse } from 'swr'
import { AutomationRuleTriggersEnum } from '../triggers/enums/trigger-enum'
import { AutomationRulesTriggersType } from '../triggers/types/automation-rule-trigger-interface'
import { AutomationRulesTriggersEditType } from '../triggers/types/automation-rule-triggers-edit-interface'
import { AutomationRuleApiInterface, RuleId } from '../types/automation-rule-interface'
import SpecificTriggerRuleCreate from './components/specific-trigger-rule-create'
import SpecificTriggerRuleEdit from './components/specific-trigger-rule-edit'
import SpecificTriggerRuleError from './components/specific-trigger-rule-error'
import SpecificTriggerRulesTable from './components/specific-trigger-rules-table'

export interface SpecificTriggerRulesProps {
  specificTriggerRulesData: SWRResponse<AutomationRuleApiInterface>
  availableTriggersData: SWRResponse<AutomationRuleTriggersEnum[]>
  noRelatedMessage: (ruleId: RuleId) => string | JSX.Element
  getRelatedAndUnrelatedTriggersData: <
    T extends AutomationRulesTriggersEditType | AutomationRulesTriggersType,
  >(
    triggers: Array<T>,
  ) => {
    relatedTriggers: T[]
    unrelatedTriggers: T[]
  }
  customAddTriggerHandler?: (
    trigger: AutomationRuleTriggersEnum,
    maxTriggerId: React.MutableRefObject<number>,
  ) => AutomationRulesTriggersEditType
}

export default function SpecificTriggerRules({
  availableTriggersData,
  getRelatedAndUnrelatedTriggersData,
  specificTriggerRulesData,
  customAddTriggerHandler,
  noRelatedMessage,
}: SpecificTriggerRulesProps) {
  const [commonError, setCommonError] = useState<string>('')

  const [selectedRuleId, setSelectedRuleId] = useState<number>()

  const clearSelectedRule = () => setSelectedRuleId(undefined)

  useEffect(() => {
    if (
      specificTriggerRulesData.error &&
      specificTriggerRulesData.error instanceof BadRequest &&
      specificTriggerRulesData.error.errors.common
    ) {
      setCommonError(specificTriggerRulesData.error.errors.common.join(''))
    }
  }, [specificTriggerRulesData.error])

  useEffect(() => {
    if (
      availableTriggersData.error &&
      availableTriggersData.error instanceof BadRequest &&
      availableTriggersData.error.errors.common
    ) {
      toast.error(availableTriggersData.error.errors.common.join(''))
    }
  }, [availableTriggersData.error])

  return (
    <>
      <SpecificTriggerRuleError error={commonError} />
      {selectedRuleId ? (
        <SpecificTriggerRuleEdit
          noRelatedMessage={noRelatedMessage}
          mutateRules={specificTriggerRulesData.mutate}
          mutateAvailableTriggers={availableTriggersData.mutate}
          clearSelectedRule={clearSelectedRule}
          ruleId={selectedRuleId}
          availableTriggers={availableTriggersData.data}
          getRelatedAndUnrelatedTriggersData={getRelatedAndUnrelatedTriggersData}
          customAddTriggerHandler={customAddTriggerHandler}
        />
      ) : (
        <>
          {!specificTriggerRulesData.error && (
            <SpecificTriggerRulesTable
              noRelatedMessage={noRelatedMessage}
              mutateAvailableTriggers={availableTriggersData.mutate}
              setSelectedRuleId={setSelectedRuleId}
              mutateRules={specificTriggerRulesData.mutate}
              rules={specificTriggerRulesData.data}
              getRelatedAndUnrelatedTriggersData={getRelatedAndUnrelatedTriggersData}
            />
          )}
          <SpecificTriggerRuleCreate
            noRelatedMessage={noRelatedMessage}
            availableTriggersData={availableTriggersData}
            getRelatedAndUnrelatedTriggersData={getRelatedAndUnrelatedTriggersData}
            specificTriggerRulesData={specificTriggerRulesData}
            customAddTriggerHandler={customAddTriggerHandler}
          />
        </>
      )}
    </>
  )
}
